import { PLAY_TYPES, NUMBER_LENGTH } from './payouts';
import {
  getActiveWindow as scheduleActiveWindow,
  isWindowOpen as scheduleIsOpen,
  type WindowScheduleConfig,
} from './windowSchedule';
import {
  getBankerNumberCap,
  normalizePlayNumber,
  validateNumberExposure,
  type NumberCapConfig,
} from './numberCaps';

export interface PlayValidationContext extends NumberCapConfig {
  isWindowOpen: boolean;
  existingPlays?: import('./numberCaps').PlayLike[];
  window?: string;
}

/**
 * Validate a CreatePlayDto before sending to the data adapter.
 * Returns null if valid, or an error key string if invalid.
 */
export function validatePlay(
  dto: { type: string; number: string; number2?: string; stake: number },
  constraints: PlayValidationContext,
) {
  const { type, number, number2, stake } = dto;
  const { perNumberCap, isWindowOpen, existingPlays = [], window } = constraints;

  if (!isWindowOpen) return 'error_window_closed';

  if (!(Object.values(PLAY_TYPES) as string[]).includes(type)) return 'error_invalid_type';

  const expectedLen = NUMBER_LENGTH[type as keyof typeof NUMBER_LENGTH];
  if (!number || number.length !== expectedLen) return 'error_invalid_number';

  if (type === PLAY_TYPES.PARLE) {
    if (!number2 || number2.length !== 2) return 'error_invalid_number2';
    if (number === number2) return 'error_parle_same_numbers';
  }

  if (!stake || stake <= 0) return 'error_invalid_stake';

  const primary = normalizePlayNumber(number, type);
  const maxSingleStake = getBankerNumberCap(primary, constraints);
  if (stake > maxSingleStake) return 'error_stake_exceeds_cap';
  if (stake > perNumberCap) return 'error_stake_exceeds_cap';

  const exposureErr = validateNumberExposure(dto, constraints, existingPlays, window);
  if (exposureErr) return exposureErr;

  return null;
}

/** Current acceptance window label for a new play. */
export function getActiveWindow(config: WindowScheduleConfig, nowUtc = new Date()) {
  return scheduleActiveWindow(config, nowUtc);
}

/**
 * Returns true if the current time is within the acceptance window
 * (after shift open, before close minus cutoff buffer).
 */
export function isWindowOpen(config: WindowScheduleConfig, nowUtc = new Date()) {
  return scheduleIsOpen(config, nowUtc);
}
